import React ,{ useEffect, useState} from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ToastDefault from "../../components/Toast";
import { useNavigate, Link } from "react-router-dom";
import { stringify } from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from 'react-helmet';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import { ForgotPasswordAction } from "../../redux/actions/UserActions";
import './forgot-password.css';

type SeoContent = {
  meta_title: string;
  meta_description?: string;
  // Add more fields as needed
};

type ForgotPasswordErrors = {
  email?: string;
};

const ForgotPassword: React.FC = () => {

    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const [soeContent, setSoeContent] = useState<SeoContent | null>(null);
    const { status, data, error, loading } = useSelector((state: any) => state.forgotPasswordState);

    const [errors, setErrors] = useState<ForgotPasswordErrors>({});
    const [email, setEmail] = useState("");
    const [loadingM, setLoadingM] = useState(false);

    //const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const validationSchema = Yup.object().shape({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    });

    const handleSubmit = async (e: React.FormEvent) => {
      setLoadingM(true);
      e.preventDefault();
      setErrors({});
      //setSuccessMessage(null);

      const formData = {
        email: (document.getElementById("email") as HTMLInputElement).value,
      };

      try {
        
        await validationSchema.validate(formData, { abortEarly: false });
        console.log("Reset link sent to:", formData.email);
        
        // dispatch action here if needed
        dispatch(ForgotPasswordAction(formData));
        //setSuccessMessage("If this email is registered, you will receive a password reset link shortly.");
      } catch (err: any) {
        if (err.inner) {
          const formErrors: ForgotPasswordErrors = {};
          err.inner.forEach((validationErr: any) => {
            const key = validationErr.path as keyof ForgotPasswordErrors;
            formErrors[key] = validationErr.message;
          });
          setErrors(formErrors);
          setTimeout(() => {
            setLoadingM(false);
          }, 200);
        }
      }
    };

    useEffect(() => {
        if(status){
            dispatch(ForgotPasswordAction('RESET'));
            toast.success(data?.message);
            setTimeout(() => {
              setLoadingM(false);
              navigate("/login");
            }, 3000);
        }
        
        if(error) {
          dispatch(ForgotPasswordAction('RESET'));
          setLoadingM(false);
        }

    }, [dispatch, status , error])

  return (
    <>
    <Helmet>
      <title>{`${soeContent?.meta_title || 'Forgot Password'}`}</title>
      <meta name="title" content={soeContent?.meta_title || 'E-learning Platform'} />
      <meta name="description" content={soeContent?.meta_description || 'Learn with curated e-learning resources'} />
      <meta name="keywords" content="e-learning, tech-cards, cheat-sheets, tutorials" />
    </Helmet>

      <Header />

      <div className="forgot-wrapper">
        <div className="forgot-card">
          {/* Image Section */}
          <div className="image-section">
            <div className="image-content">
              <h2>Reset Your Password</h2>
              <p>
                Don't worry! We'll help you regain access to your account. Just
                enter your email address and we'll send you a secure link to
                reset your password.
              </p>
              <div className="mt-4">
                <i className="bi bi-shield-lock fs-1"></i>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="form-section">
            <div className="text-center mb-4">
              <i className="bi bi-key-fill text-primary fs-1"></i>
              <h3 className="fw-bold mt-3">Forgot Password</h3>
              <p className="info-text">
                Enter your email address and we'll send you a link to reset your
                password.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="mb-4">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              {/* Submit Button */}
              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg fw-semibold"
                  disabled={loadingM}
                >
                  {loadingM ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Logging in...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-envelope-fill me-2"></i> Send Reset Link
                    </>
                  )}
                </button>
              </div>

              {/* Back to login */}
              <div className="text-center mt-4">
                <Link
                  to={"/login"}
                  className="text-decoration-none text-primary"
                >
                  <i className="bi bi-arrow-left"></i> Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastDefault/>
      <Footer />
    </>
  );
};

export default ForgotPassword;
